import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndDownLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Button} from '../../atoms/Buttons/Button';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Image} from '../../atoms/Image/Image';
import {Text} from '../../atoms/Text';

export interface WebCouponBannerProps {
  ['data-testid']?: string;
  className?: string;
  deviceImage: string;
  deviceSize?: number;
  text: string;
  textSize?: keyof typeof FONT_SIZES;
  ctaText?: string;
  CTALink?: string;
  onCTAClick?: VoidFunction;
  showCTAasText?: boolean;
  showInfoIcon?: boolean;
  infoIconSize?: keyof typeof IconSize;
  onInfoIconClick?: VoidFunction;
  qrCodeImage?: string;
  StoreBadgesComponent?: React.ReactNode;
  storeBadgesPosition?: 'center' | 'end';
  bannerBackgroundSvg?: string;
}

export const WebCouponBanner = ({
  'data-testid': dataTestId = 'webCouponBanner',
  className,
  deviceImage,
  deviceSize = 134,
  text,
  textSize = 'px12',
  ctaText,
  CTALink,
  onCTAClick,
  showCTAasText = false,
  showInfoIcon = true,
  infoIconSize = 'XSS',
  onInfoIconClick,
  qrCodeImage,
  StoreBadgesComponent,
  storeBadgesPosition = 'center',
  bannerBackgroundSvg,
  ref,
}: WebCouponBannerProps & {ref?: React.Ref<HTMLDivElement>}) => {
  return (
    <Wrapper
      data-testid={dataTestId}
      className={className}
      ref={ref}
      $bannerBackgroundSvg={bannerBackgroundSvg}
    >
      <DevicesImage
        src={deviceImage}
        $deviceSize={deviceSize}
        breakpoints={[{columns: 3}]}
        widths={[deviceSize, deviceSize * 2, deviceSize * 3]}
        ratio="1.34:1"
      />
      <ContentWrapper>
        <Content size={textSize} dangerouslySetInnerHTML={{__html: text}} />
        {StoreBadgesComponent && storeBadgesPosition === 'center'
          ? StoreBadgesComponent
          : null}
        {ctaText && (
          <WebCouponCTA
            dataTestId={`${dataTestId}CTA`}
            ctaText={ctaText}
            CTALink={CTALink}
            onClick={onCTAClick}
            showCTAasText={showCTAasText}
            textSize={textSize}
          />
        )}
      </ContentWrapper>
      <EndContent>
        {qrCodeImage && (
          <QRCode
            src={qrCodeImage}
            breakpoints={[]}
            widths={[100, 200]}
            ratio="1:1"
          />
        )}
        {StoreBadgesComponent && storeBadgesPosition === 'end'
          ? StoreBadgesComponent
          : null}
      </EndContent>
      {showInfoIcon && (
        <InfoIcon
          data-testid={`${dataTestId}InfoIcon`}
          onClick={onInfoIconClick}
        >
          <Icon
            asset={GenericIconName.InfoWithoutFill}
            size={infoIconSize}
            fill={COLORS.PrimaryCoupon}
          />
        </InfoIcon>
      )}
    </Wrapper>
  );
};

const WebCouponCTA: React.FC<{
  dataTestId: string;
  ctaText: string;
  CTALink?: string;
  onClick?: VoidFunction;
  showCTAasText?: boolean;
  textSize?: keyof typeof FONT_SIZES;
}> = ({dataTestId, ctaText, CTALink, onClick, showCTAasText, textSize}) => {
  return showCTAasText ? (
    <DownloadText size={textSize}>{ctaText}</DownloadText>
  ) : (
    <DownloadAppButton
      data-testid={dataTestId}
      href={CTALink}
      onClick={onClick}
      aria-label={undefined}
    >
      <DownloadIcon
        asset={GenericIconName.Download}
        fill={COLORS.White}
        size="XS"
      />
      {ctaText}
    </DownloadAppButton>
  );
};

const Wrapper = styled.div<{$bannerBackgroundSvg?: string}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 14px;
  background-image: ${props =>
    props.$bannerBackgroundSvg ? `url(${props.$bannerBackgroundSvg})` : ''};
  background-repeat: no-repeat;
  background-size: cover;
  color: ${COLORS.PrimaryCoupon};
  overflow: hidden;
`;

export const DevicesImage = styled(Image)<{$deviceSize: number}>`
  width: ${props => props.$deviceSize}px;
  align-self: flex-end;
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 16px 10px 16px 1%;
`;

const Content = styled(Text)`
  color: ${COLORS.PrimaryCoupon};
  line-height: 1.3;

  ${tabletAndDownLinaria(`
    font-size: ${FONT_SIZES.px12};
  `)}

  b {
    font-weight: ${FONT_WEIGHTS.heavy};
  }
`;

const DownloadText = styled(Text)`
  color: ${COLORS.PrimaryCoupon};
  font-weight: ${FONT_WEIGHTS.heavy};
  margin-top: 1rem;
`;

const DownloadAppButton = styled(Button)`
  && {
    --border-width: 0;
    --background-color: ${COLORS.PrimaryCoupon};

    &:hover {
      --background-color: ${COLORS.PrimaryCoupon};
    }

    padding: 6px 0;
    margin-top: 0.75rem;
    font-size: ${FONT_SIZES.px10};
    font-weight: ${FONT_WEIGHTS.light};
    color: ${COLORS.White};
  }
`;

const DownloadIcon = styled(Icon)`
  margin-right: 4px;
`;

const InfoIcon = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  padding: 4px;
  cursor: pointer;
`;

const EndContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const QRCode = styled(Image)`
  width: 100px;
  margin-left: 16px;
`;
